// POST /auth/refresh — slide the session cookie forward + probe session
// validity (F2 / #808).
//
// The session is a 24 h HttpOnly cookie that the backend RE-ISSUES on this call
// (sliding session — see apps/backend/src/auth/refresh.ts). Two callers:
//   - the session keep-alive (`slideSession`, app/session-keepalive.tsx):
//     best-effort, fired on authenticated activity to push the 24 h expiry
//     forward so a long field shift never crosses it mid-work.
//   - the upload-path auth recovery (powersync/connector-upload-auth.ts): on an
//     upload 401 it calls `refreshSession` to distinguish a transient blip
//     (`ok:true`) from genuine session expiry (`ok:false`) so it can pause +
//     surface re-auth instead of wedging the whole device queue.

import { ApiError, apiFetch } from './api'
import { resumeUploadsAfterReauth } from './powersync/upload-pause-latch'

export interface SessionRefreshResult {
  /** True when the session is still valid (cookie re-issued / slid forward). */
  ok: boolean
  /** Unix milliseconds the session now expires; present only when `ok`. */
  sessionExpiresAt?: number
}

interface RefreshResponseBody {
  sessionExpiresAt?: number
}

/**
 * Re-issue (slide) the session cookie from the current authenticated identity.
 *   - 2xx           → `{ ok: true, sessionExpiresAt }`.
 *   - 401           → `{ ok: false }` — the session genuinely expired (the auth
 *                     handler rejected the cookie). A caller-actionable signal,
 *                     not an exception.
 *   - anything else → RETHROWN (5xx / network / unexpected) so callers treat it
 *                     as transient (retryable), never as "session gone".
 */
export async function refreshSession(): Promise<SessionRefreshResult> {
  try {
    const body = await apiFetch<RefreshResponseBody>('/auth/refresh', {
      method: 'POST',
    })
    // A 2xx proves the session is valid again, so lift any upload pause an
    // earlier 401 set (F2/#808 stuck-latch fix — audit-1 #1). This is the SINGLE
    // convergent resume point: it makes a keep-alive slide (slideSession) clear
    // the pause too, not only an upload-path-triggered refresh — so a session
    // that recovers WITHOUT a full /auth/login reload still resumes uploads.
    resumeUploadsAfterReauth()
    return { ok: true, sessionExpiresAt: body?.sessionExpiresAt }
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 401 || err.code === 'unauthenticated')
    ) {
      return { ok: false }
    }
    throw err
  }
}

/**
 * Best-effort slide for the keep-alive. A failed slide just means the cookie
 * keeps its current expiry and the next activity retries — so this never throws
 * and never surfaces UI. (The upload-path recovery uses `refreshSession`
 * directly because it must branch on the outcome.)
 */
export async function slideSession(): Promise<void> {
  try {
    await refreshSession()
  } catch {
    // Swallow — sliding is best-effort; the next trigger retries.
  }
}
